import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Text,
} from '@mantine/core';
import { ArrowsClockwiseIcon, WarningIcon } from '@phosphor-icons/react';
import {
  type CharacterOptions,
  type MemorableOptions,
  type PassphraseOptions,
  MEMORABLE_SYMBOLS,
  MEMORABLE_WORDS_MAX,
  MEMORABLE_WORDS_MIN,
  PASSPHRASE_WORDS_MAX,
  PASSPHRASE_WORDS_MIN,
  PASSWORD_LENGTH_MAX,
  PASSWORD_LENGTH_MIN,
  generateMemorable,
  generatePassphrase,
  generatePassword,
  poolSize,
} from '@/lib/password';
import {
  classifyStrength,
  memorableEntropyBits,
  passphraseEntropyBits,
  passwordEntropyBits,
} from '@/lib/entropy';
import { WORDLIST_SIZE, loadWordlist } from '@/lib/wordlists';
import { useLang, useStrings } from '@/i18n/useLang';
import { StrengthMeter } from './StrengthMeter';
import { SecretOutput } from './SecretOutput';

type Mode = 'chars' | 'phrase' | 'memorable';

const needsWords = (mode: Mode) => mode === 'phrase' || mode === 'memorable';

const PRESETS: Record<string, Partial<CharacterOptions>> = {
  good: { length: 16, lowercase: true, uppercase: true, digits: true, symbols: false, excludeAmbiguous: true },
  strong: { length: 24, lowercase: true, uppercase: true, digits: true, symbols: true, excludeAmbiguous: false },
  paranoid: { length: 40, lowercase: true, uppercase: true, digits: true, symbols: true, excludeAmbiguous: false },
};

export function PasswordGenerator() {
  const t = useStrings();
  const lang = useLang();

  const [mode, setMode] = useState<Mode>('chars');
  const [charOpts, setCharOpts] = useState<CharacterOptions>({
    length: 20,
    lowercase: true,
    uppercase: true,
    digits: true,
    symbols: true,
    excludeAmbiguous: false,
  });
  const [phraseOpts, setPhraseOpts] = useState<PassphraseOptions>({
    wordCount: 4,
    separator: '-',
    capitalize: true,
    includeNumber: true,
  });
  const [memorableOpts, setMemorableOpts] = useState<MemorableOptions>({
    wordCount: 3,
    separator: '-',
  });

  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [words, setWords] = useState<string[] | null>(null);

  // Lazy-load the word list for the active language when a word-based mode is used.
  useEffect(() => {
    if (!needsWords(mode)) return;
    let cancelled = false;
    loadWordlist(lang)
      .then((w) => {
        if (!cancelled) {
          setWords(w);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWords(null);
          setError(t.pw.wordlistError);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mode, lang, t.pw.wordlistError]);

  const generate = useCallback(() => {
    if (mode === 'chars') {
      if (poolSize(charOpts) === 0) {
        setError(t.pw.noClass);
        setOutput('');
        return;
      }
      setError(null);
      setOutput(generatePassword(charOpts));
      return;
    }
    if (!words) return; // still loading; the load effect will trigger generation
    setError(null);
    setOutput(
      mode === 'memorable'
        ? generateMemorable(words, memorableOpts)
        : generatePassphrase(words, phraseOpts),
    );
  }, [mode, charOpts, phraseOpts, memorableOpts, words, t.pw.noClass]);

  // Regenerate whenever inputs change (and once words become available).
  useEffect(() => {
    generate();
  }, [generate]);

  const bits =
    mode === 'chars'
      ? passwordEntropyBits(charOpts.length, poolSize(charOpts))
      : mode === 'memorable'
        ? memorableEntropyBits(memorableOpts.wordCount, WORDLIST_SIZE, MEMORABLE_SYMBOLS.length)
        : passphraseEntropyBits(phraseOpts.wordCount, WORDLIST_SIZE, phraseOpts.includeNumber);
  const strength = classifyStrength(bits);

  const updateChar = (patch: Partial<CharacterOptions>) =>
    setCharOpts((o) => ({ ...o, ...patch }));

  return (
    <Stack gap="lg">
      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(v) => setMode(v as Mode)}
        data={[
          { value: 'chars', label: t.pw.modeChars },
          { value: 'phrase', label: t.pw.modePhrase },
          { value: 'memorable', label: t.pw.modeMemorable },
        ]}
      />

      <SecretOutput value={output} />
      {error && (
        <Alert color="orange" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}
      <StrengthMeter strength={strength} />

      {mode === 'chars' ? (
        <Stack gap="md">
          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={500}>{t.pw.length}</Text>
              <Text fw={700} c="cyan">
                {charOpts.length}
              </Text>
            </Group>
            <Slider
              min={PASSWORD_LENGTH_MIN}
              max={PASSWORD_LENGTH_MAX}
              value={charOpts.length}
              onChange={(length) => updateChar({ length })}
              aria-label={t.pw.length}
            />
          </div>

          <Group>
            <Checkbox
              label={t.pw.lowercase}
              checked={charOpts.lowercase}
              onChange={(e) => updateChar({ lowercase: e.currentTarget.checked })}
            />
            <Checkbox
              label={t.pw.uppercase}
              checked={charOpts.uppercase}
              onChange={(e) => updateChar({ uppercase: e.currentTarget.checked })}
            />
            <Checkbox
              label={t.pw.digits}
              checked={charOpts.digits}
              onChange={(e) => updateChar({ digits: e.currentTarget.checked })}
            />
            <Checkbox
              label={t.pw.symbols}
              checked={charOpts.symbols}
              onChange={(e) => updateChar({ symbols: e.currentTarget.checked })}
            />
          </Group>
          <Checkbox
            label={t.pw.excludeAmbiguous}
            checked={charOpts.excludeAmbiguous}
            onChange={(e) => updateChar({ excludeAmbiguous: e.currentTarget.checked })}
          />

          <div>
            <Text fw={500} mb={6}>
              {t.pw.presets}
            </Text>
            <Group>
              <Button variant="default" size="xs" onClick={() => updateChar(PRESETS.good)}>
                {t.pw.presetGood}
              </Button>
              <Button variant="default" size="xs" onClick={() => updateChar(PRESETS.strong)}>
                {t.pw.presetStrong}
              </Button>
              <Button variant="default" size="xs" onClick={() => updateChar(PRESETS.paranoid)}>
                {t.pw.presetParanoid}
              </Button>
            </Group>
          </div>
        </Stack>
      ) : mode === 'phrase' ? (
        <Stack gap="md">
          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={500}>{t.pw.words}</Text>
              <Text fw={700} c="cyan">
                {phraseOpts.wordCount}
              </Text>
            </Group>
            <Slider
              min={PASSPHRASE_WORDS_MIN}
              max={PASSPHRASE_WORDS_MAX}
              value={phraseOpts.wordCount}
              onChange={(wordCount) => setPhraseOpts((o) => ({ ...o, wordCount }))}
              aria-label={t.pw.words}
            />
          </div>
          <Select
            label={t.pw.separator}
            value={phraseOpts.separator}
            onChange={(v) => v && setPhraseOpts((o) => ({ ...o, separator: v }))}
            data={[
              { value: '-', label: t.pw.sepHyphen },
              { value: '.', label: t.pw.sepDot },
              { value: ' ', label: t.pw.sepSpace },
            ]}
            allowDeselect={false}
            maw={240}
          />
          <Group>
            <Checkbox
              label={t.pw.capitalize}
              checked={phraseOpts.capitalize}
              onChange={(e) => setPhraseOpts((o) => ({ ...o, capitalize: e.currentTarget.checked }))}
            />
            <Checkbox
              label={t.pw.includeNumber}
              checked={phraseOpts.includeNumber}
              onChange={(e) =>
                setPhraseOpts((o) => ({ ...o, includeNumber: e.currentTarget.checked }))
              }
            />
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t.pw.memorableHint}
          </Text>
          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={500}>{t.pw.words}</Text>
              <Text fw={700} c="cyan">
                {memorableOpts.wordCount}
              </Text>
            </Group>
            <Slider
              min={MEMORABLE_WORDS_MIN}
              max={MEMORABLE_WORDS_MAX}
              value={memorableOpts.wordCount}
              onChange={(wordCount) => setMemorableOpts((o) => ({ ...o, wordCount }))}
              aria-label={t.pw.words}
            />
          </div>
          <Select
            label={t.pw.separator}
            value={memorableOpts.separator}
            onChange={(v) => v && setMemorableOpts((o) => ({ ...o, separator: v }))}
            data={[
              { value: '-', label: t.pw.sepHyphen },
              { value: '.', label: t.pw.sepDot },
            ]}
            allowDeselect={false}
            maw={240}
          />
        </Stack>
      )}

      <Button
        leftSection={<ArrowsClockwiseIcon size={18} />}
        onClick={generate}
        size="md"
        disabled={needsWords(mode) && !words}
      >
        {t.pw.regenerate}
      </Button>
    </Stack>
  );
}
